/**
 * 🌉 Unified Primitives for Cross-Platform Development
 * Bridge between web HTML and React Native components
 * 
 * Usage: Import these instead of directly using div/span/img for new components
 * Legacy components will be migrated gradually
 */

import React from 'react';
import { 
  View as RNView, 
  Text as RNText, 
  Image as RNImage, 
  TouchableOpacity,
  Pressable,
  ScrollView as RNScrollView,
  Platform,
  StyleSheet,
  ViewStyle,
  TextStyle,
  ImageStyle
} from 'react-native';

interface UnifiedViewProps {
  children?: React.ReactNode;
  className?: string;
  style?: ViewStyle | any;
  onClick?: () => void;
  onPress?: () => void;
  [key: string]: any;
}

/**
 * Unified View component - renders div on web, View on native
 */
export const UnifiedView: React.FC<UnifiedViewProps> = ({ 
  children, 
  className, 
  style, 
  onClick, 
  onPress,
  ...props 
}) => {
  if (Platform.OS === 'web') {
    return (
      <div 
        className={className} 
        style={style}
        onClick={onClick || onPress}
        {...props}
      >
        {children}
      </div>
    );
  }

  const handlePress = onClick || onPress;

  if (handlePress) {
    return (
      <Pressable onPress={handlePress} style={style} {...props}>
        {children}
      </Pressable>
    );
  }

  return (
    <RNView style={style} {...props}>
      {children}
    </RNView>
  );
};

interface UnifiedTextProps {
  children?: React.ReactNode;
  className?: string;
  style?: TextStyle | any;
  [key: string]: any;
}

/**
 * Unified Text component - renders span/p on web, Text on native
 */
export const UnifiedText: React.FC<UnifiedTextProps> = ({ 
  children, 
  className, 
  style,
  ...props 
}) => {
  if (Platform.OS === 'web') {
    return (
      <span className={className} style={style} {...props}>
        {children}
      </span>
    );
  }

  return (
    <RNText style={style} {...props}>
      {children}
    </RNText>
  );
};

interface UnifiedImageProps {
  src?: string;
  source?: any;
  alt?: string;
  className?: string;
  style?: ImageStyle | any;
  [key: string]: any;
}

/**
 * Unified Image component - renders img on web, Image on native
 */
export const UnifiedImage: React.FC<UnifiedImageProps> = ({ 
  src, 
  source,
  alt, 
  className, 
  style,
  ...props 
}) => {
  if (Platform.OS === 'web') {
    return (
      <img 
        src={src} 
        alt={alt || ''} 
        className={className} 
        style={style} 
        {...props}
      />
    );
  }

  // For native, prefer source prop, but convert src if provided
  const imageSource = source || (src ? { uri: src } : undefined);

  return (
    <RNImage 
      source={imageSource}
      style={style} 
      accessibilityLabel={alt}
      {...props}
    />
  );
};

interface UnifiedButtonProps {
  children?: React.ReactNode;
  className?: string;
  style?: ViewStyle | any;
  onClick?: () => void;
  onPress?: () => void;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * Unified Button component - renders button on web, TouchableOpacity on native
 */
export const UnifiedButton: React.FC<UnifiedButtonProps> = ({ 
  children, 
  className, 
  style, 
  onClick, 
  onPress,
  disabled,
  ...props 
}) => {
  const handlePress = onClick || onPress;

  if (Platform.OS === 'web') {
    return (
      <button 
        className={className} 
        style={style}
        onClick={handlePress}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }

  return (
    <TouchableOpacity 
      onPress={handlePress} 
      style={[style, disabled && styles.disabledButton]} 
      disabled={disabled}
      {...props}
    >
      {children}
    </TouchableOpacity>
  );
};

interface UnifiedScrollViewProps {
  children?: React.ReactNode;
  className?: string;
  style?: ViewStyle | any;
  [key: string]: any;
}

/**
 * Unified ScrollView component - renders div with overflow on web, ScrollView on native
 */
export const UnifiedScrollView: React.FC<UnifiedScrollViewProps> = ({ 
  children, 
  className, 
  style,
  ...props 
}) => {
  if (Platform.OS === 'web') {
    return (
      <div 
        className={className} 
        style={{ overflow: 'auto', ...style }}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <RNScrollView style={style} {...props}>
      {children}
    </RNScrollView>
  );
};

const styles = StyleSheet.create({
  disabledButton: {
    opacity: 0.5,
  },
});

// Export all primitives
export {
  UnifiedView as View,
  UnifiedText as Text,
  UnifiedImage as Image,
  UnifiedButton as Button,
  UnifiedScrollView as ScrollView,
};

